/**
 * BroadcastHealth — is the picture actually getting out?
 *
 * "Go Live" proves nothing. The encoder may never have connected, the phone may
 * have dropped off mid-sermon, the provider may be sitting on a black screen.
 * This strip answers the only two questions a media desk has while a service is
 * on: is video arriving, and is anybody receiving it.
 *
 * It refreshes every ten seconds while the panel is open and stops when it is
 * not, so an admin screen left open all afternoon does not interrogate the
 * streaming server for nothing. Where the truth cannot be known — YouTube and
 * Facebook will not tell us — it says so rather than showing a confident tick.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Radio, Eye, AlertTriangle, CheckCircle2, HelpCircle, Loader2 } from 'lucide-react';

import api from '@/lib/api';

const elapsed = (since) => {
  if (!since) return '';
  const secs = Math.max(Math.floor(Date.now() / 1000 - Number(since)), 0);
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)} min`;
  return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
};

export const BroadcastHealth = ({ scope, ident, active = true }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['broadcast-health', scope, ident],
    queryFn: () => api.get(`/live/${scope}/${ident}/health`).then((r) => r.data),
    enabled: !!scope && !!ident && active,
    refetchInterval: active ? 10000 : false,
  });

  if (isLoading && !data) {
    return (
      <p className="text-[11px] text-slate-400" data-testid="broadcast-health-loading">
        <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />Checking the broadcast…
      </p>
    );
  }
  if (!data) return null;

  const arriving = data.video_arriving === true;
  const unknown = data.video_arriving === null || data.source === 'external';

  const tone = arriving
    ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
    : unknown
      ? 'border-slate-200 bg-slate-50 text-slate-700'
      : 'border-amber-200 bg-amber-50 text-amber-900';
  const Icon = arriving ? CheckCircle2 : unknown ? HelpCircle : AlertTriangle;

  return (
    <div className={`rounded-lg border px-2.5 py-2 ${tone}`}
         data-testid={`broadcast-health-${scope}-${ident}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="flex items-center gap-1.5 text-xs font-semibold"
              data-testid="broadcast-health-state">
          <Icon className="h-3.5 w-3.5" />
          {arriving ? 'Video is arriving' : unknown ? 'Cannot tell from here' : 'No video yet'}
        </span>
        {arriving && (
          <>
            <span className="flex items-center gap-1 text-xs" data-testid="broadcast-health-publishers">
              <Radio className="h-3 w-3" />{data.publishers} sending
            </span>
            <span className="flex items-center gap-1 text-xs" data-testid="broadcast-health-viewers">
              <Eye className="h-3 w-3" />{data.viewers} watching
            </span>
            {data.since ? (
              <span className="text-xs opacity-70" data-testid="broadcast-health-since">
                on air {elapsed(data.since)}
              </span>
            ) : null}
          </>
        )}
      </div>
      <p className="mt-1 text-[11px] leading-snug opacity-80" data-testid="broadcast-health-note">
        {data.note}
      </p>
    </div>
  );
};

export default BroadcastHealth;
